import { Box, IconButton, Stack, Typography } from "@mui/material";
import { useState } from "react";
import { v4 as uuid } from "uuid";
import useBankInfoList from "src/hooks/bank-info/useBankInfoList";
import UpdateIcon from "src/components/shared/icon/UpdateIcon";
import DeleteIcon from "src/components/shared/icon/DeleteIcon";
import BankInfoForm from "../BankInfoForm";
import useAppGlobal from "src/context/useAppGlobal";
import BankInfoConfirmDelete from "../BankInfoConfirmDelete";
import { PERMISSIONS, useRoleCheck } from "src/hooks/permission/permission";

const BANK_LOGO_FRAME_PX = 44;

export default function useBankInfoBody() {
  const permissionCheck = useRoleCheck();

  const [pagi, setPagi] = useState({
    limit: 50,
    skip: 0,
  });
  const { handleOpenDialog, closeDialog } = useAppGlobal();
  const {
    data: bankInfos,
    error,
    isLoading,
  } = useBankInfoList({
    ...pagi,
    sortField: "createdAt",
    sortOrder: "asc",
  });

  const header = [
    {
      id: uuid(),
      title: "STT",
      width: "20px",
      align: "center",
    },
    {
      id: uuid(),
      title: "Tên ngân hàng",
      width: "120px",
      align: "center",
    },
    {
      id: uuid(),
      title: "Số tài khoản",
      width: "120px",
      align: "center",
    },
    {
      id: uuid(),
      title: "Chủ tài khoản",
      width: "120px",
      align: "center",
    },
    {
      id: uuid(),
      title: "Thao tác",
      width: "80px",
      align: "center",
    },
  ];

  const handleUpdate = (item) => {
    handleOpenDialog({
      title: "Cập nhật tài khoản ngân hàng",
      size: "md",
      content: <BankInfoForm item={item} />,
    });
  };

  const handleConfirmDelete = (item) => {
    handleOpenDialog({
      title: "Xoá tài khoản ngân hàng",
      content: (
        <BankInfoConfirmDelete
          item={item}
          onClose={closeDialog}
        />
      ),
      size: "md",
    });
  };

  const bodyList =
    Array.isArray(bankInfos?.data) && bankInfos?.data?.length
      ? bankInfos.data.map((v, i) => ({
          ...v,
          no: i + pagi.skip + 1,
          bankName: (() => {
            const fullName = v?.bank?.name ?? v?.bankName ?? "";
            const shortName = v?.bank?.shortName ?? "";
            const textLine = [shortName, fullName].filter(Boolean).join(" - ") || "-";
            const hasLogo = Boolean(v?.bank?.logo);
            return (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="center"
                spacing={0.75}
                sx={{ py: 0.5, textAlign: "left" }}
              >
                {hasLogo ? (
                  <Box
                    sx={{
                      width: BANK_LOGO_FRAME_PX,
                      height: BANK_LOGO_FRAME_PX,
                      flexShrink: 0,
                      boxSizing: "border-box",
                      border: "1px solid",
                      borderColor: "divider",
                      borderRadius: 1,
                      p: 0,
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      bgcolor: "background.paper",
                    }}
                  >
                    <Box
                      component="img"
                      src={v.bank.logo}
                      alt=""
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                      }}
                      sx={{
                        width: "100%",
                        height: "100%",
                        maxWidth: "100%",
                        maxHeight: "100%",
                        objectFit: "contain",
                        display: "block",
                      }}
                    />
                  </Box>
                ) : null}
                {hasLogo && textLine !== "-" ? (
                  <Typography
                    variant="body2"
                    color="text.disabled"
                    sx={{ flexShrink: 0, userSelect: "none" }}
                    aria-hidden
                  >
                    {" "}
                    -{" "}
                  </Typography>
                ) : null}
                <Typography
                  variant="body2"
                  sx={{ minWidth: 0, wordBreak: "break-word" }}
                >
                  {textLine}
                </Typography>
              </Stack>
            );
          })(),
          accountNumber: (
            <Box>
              <Typography variant="body2">{v?.accountNumber ?? "-"}</Typography>
            </Box>
          ),
          ownerName: (
            <Box>
              <Typography variant="body2">{v?.ownerName ?? "-"}</Typography>
            </Box>
          ),
          action: (
            <Box
              display="flex"
              justifyContent="center"
              gap="8px"
            >
              <IconButton
                disabled={!permissionCheck([PERMISSIONS.UPDATE_BANKINFO])}
                onClick={() => handleUpdate(v)}
                sx={{ background: "var(--grey) !important", color: "#000" }}
              >
                <UpdateIcon />
              </IconButton>
              <IconButton
                disabled={!permissionCheck([PERMISSIONS.DELETE_BANKINFO])}
                onClick={() => handleConfirmDelete(v)}
                sx={{ background: "var(--grey) !important", color: "#000" }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>
          ),
        }))
      : null;

  const sortData = ["no", "bankName", "accountNumber", "ownerName", "action"];

  return {
    header,
    isLoading,
    error,
    setPagi,
    bodyList,
    sortData,
    total: bankInfos?.count,
  };
}
